function token() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}

function headers(json = false) {
  const result = {};
  const value = token();
  if (value) result.Authorization = `Bearer ${value}`;
  if (json) result["Content-Type"] = "application/json";
  return result;
}

async function request(url, options = {}) {
  const response = await fetch(url, { ...options, headers: { ...headers(Boolean(options.body)), ...(options.headers || {}) } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const gatewayMessage = response.status === 504
      ? "분류 작업 시간이 길어졌습니다. 잠시 후 다시 시도해 주세요. (504)"
      : [502, 503].includes(response.status)
        ? "분류 서버에 일시적으로 연결할 수 없습니다. 잠시 후 다시 시도해 주세요."
        : "";
    throw new Error(body.error || gatewayMessage || `작업공간 요청에 실패했습니다. (${response.status})`);
  }
  return body;
}

export function workspaceIdForQuery(query) {
  const normalized = String(query || "").trim().toLocaleLowerCase().replace(/\s+/g, " ");
  let hash = 2166136261;
  for (let i = 0; i < normalized.length; i += 1) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `review-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export function canUseWorkspaceApi() {
  return Boolean(token());
}

export async function listWorkspaces({ archived = false, deleted = false } = {}) {
  const params = new URLSearchParams({ archived: String(archived), deleted: String(deleted) });
  return request(`/api/onto/classification-workspaces?${params}`);
}

export function getWorkspace(workspaceId) {
  return request(`/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}`);
}

export function ensureWorkspace(query, activeMode = "auto", options = {}) {
  const value = String(query || "").trim();
  if (!value) return Promise.resolve(null);
  return request("/api/onto/classification-workspaces", {
    method: "POST",
    body: JSON.stringify({
      workspace_id: options.workspaceId || workspaceIdForQuery(value),
      title: options.title || value,
      query_text: value,
      active_mode: activeMode,
      state: options.state || {},
    }),
  });
}

export function updateWorkspace(workspaceId, changes) {
  return request(`/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}`, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
}

export function deleteWorkspace(workspaceId) {
  return request(`/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}`, { method: "DELETE" });
}

export function restoreWorkspace(workspaceId) {
  return request(`/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/restore`, { method: "POST" });
}

export function addWorkspaceNote(workspaceId, body) {
  return request(`/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/notes`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export function deleteWorkspaceNote(workspaceId, noteId) {
  return request(`/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });
}

export function addWorkspaceEvidence(workspaceId, evidence) {
  return request(`/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/evidence`, {
    method: "POST",
    body: JSON.stringify(evidence),
  });
}

export function deleteWorkspaceEvidence(workspaceId, evidenceId) {
  return request(`/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/evidence/${encodeURIComponent(evidenceId)}`, { method: "DELETE" });
}

export async function uploadWorkspaceAttachment(workspaceId, file) {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(
    `/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/attachments`,
    { method: "POST", headers: headers(false), body: form },
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `attachment upload failed (${response.status})`);
  return body;
}

export function analyzeWorkspaceAttachment(workspaceId, evidenceId) {
  return request(
    `/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/attachments/${encodeURIComponent(evidenceId)}/analyze`,
    { method: "POST" },
  );
}

export function runWorkspaceAssistant(workspaceId, payload) {
  return request(
    `/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/assistant`,
    { method: "POST", body: JSON.stringify(payload || {}) },
  );
}

export async function downloadWorkspaceAttachment(workspaceId, evidenceId, fileName) {
  const response = await fetch(
    `/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/attachments/${encodeURIComponent(evidenceId)}`,
    { headers: headers(false) },
  );
  if (!response.ok) throw new Error(`attachment download failed (${response.status})`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "attachment";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function deleteWorkspaceAttachment(workspaceId, evidenceId) {
  return request(
    `/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/attachments/${encodeURIComponent(evidenceId)}`,
    { method: "DELETE" },
  );
}

export function linkWorkspaceMaster(workspaceId, data) {
  return request(`/api/onto/classification-workspaces/${encodeURIComponent(workspaceId)}/master-hs`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

const MIGRATION_DONE_KEY = "loginhs:workspace-migration-done";
const MIGRATION_MAX = 25;
const migratedQueries = new Set();

function migrationDone() {
  try { return sessionStorage.getItem(MIGRATION_DONE_KEY) === "1"; } catch { return false; }
}

function markMigrationDone() {
  try { sessionStorage.setItem(MIGRATION_DONE_KEY, "1"); } catch { /* ignore */ }
}

/** 테스트·재로그인용: 이 탭의 이관 완료 표시를 지운다. */
export function resetLocalReviewMigration() {
  migratedQueries.clear();
  try { sessionStorage.removeItem(MIGRATION_DONE_KEY); } catch { /* ignore */ }
}

/**
 * 로컬 최근 검토를 서버 작업공간으로 이관한다. 과거에는 사이드바가 갱신될 때마다 최근 검토
 * 전부를 동시에 POST해서(탭 수 × 최근 검토 수) 서버 DB 풀(5개)이 30초 대기에 걸리고 전체가
 * 멈췄다(2026-09-02 장애). 이제 탭 세션당 한 번만, 순차로, 최대 25건까지만 보낸다.
 */
export async function migrateLocalReviews(recents, pinned, options = {}) {
  if (!canUseWorkspaceApi()) return;
  if (!options.force && migrationDone()) return;
  const pinnedSet = new Set(pinned || []);
  const queue = (recents || [])
    .map((query) => String(query || "").trim())
    .filter((query) => query && !migratedQueries.has(query))
    .slice(0, MIGRATION_MAX);
  for (const query of queue) {
    try {
      const item = await ensureWorkspace(query, "auto");
      migratedQueries.add(query);
      if (item && pinnedSet.has(query) && !item.pinned) {
        await updateWorkspace(item.workspaceId, { pinned: true });
      }
    } catch {
      // 한 건이 실패해도 다음 건은 계속 보내되, 이 탭에서 다시 폭주하지 않도록 완료로 표시한다.
    }
  }
  markMigrationDone();
}
